import { type FunctionComponent, useCallback, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useHistory } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'
import { GearIcon } from '@instacart/ids-core'
import { colors } from '@retailer-platform/shared-components'
import { FilterSelectorRow, IconButton, Text } from '@retailer-platform/shared-components/src/tds'
import { usePartnerContext, useCurrentAccountContext } from '@retailer-platform/dashboard/utils'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import { routes } from '../../routing/routes'
import { useDomainMessages } from '../../utils/domain/intl'
import { NotificationFilterType } from '../../hooks/useNotificationFeed'
import { NotificationFeed } from '../notifications-drawer/NotificationFeed'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const Panel = styled.div({
  width: 400,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'min(640px, calc(100vh - 96px))',
  backgroundColor: colors.GRAYSCALE.X0,
})

const Header = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px 12px',
  gap: 8,
})

const FilterRow = styled.div({
  padding: '0 16px 12px',
  borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
})

const FeedScroll = styled.div({
  overflowY: 'auto',
  flex: 1,
})

export const NotificationsDropdown: FunctionComponent<Props> = ({ isOpen, onClose }) => {
  const history = useHistory()
  const partnerContext = usePartnerContext()
  const { account } = useCurrentAccountContext()
  const [activeFilter, setActiveFilter] = useQueryParam('filter', StringParam)
  const previousFilterRef = useRef<string | null>(null)

  const i18n = useDomainMessages({
    title: 'notificationsDomain.drawer.title',
    settingsButton: 'notificationsDomain.drawer.button.settings',
    all: 'notificationsDomain.drawer.filter.all',
    urgent: 'notificationsDomain.drawer.filter.urgent',
    unread: 'notificationsDomain.drawer.filter.unread',
    today: 'notificationsDomain.drawer.filter.today',
    earlier: 'notificationsDomain.drawer.filter.earlier',
  })

  const filters = [
    { label: i18n.all, value: NotificationFilterType.All },
    { label: i18n.urgent, value: NotificationFilterType.Urgent },
    { label: i18n.unread, value: NotificationFilterType.Unread },
    { label: i18n.today, value: NotificationFilterType.Today },
    { label: i18n.earlier, value: NotificationFilterType.Earlier },
  ]

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        id: 'notifications.drawer.opened',
        description: 'User opened the notifications drawer',
        data: {
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
    }
  }, [isOpen, partnerContext.partnerId, account.id])

  const onSettingsClick = useCallback(() => {
    if (partnerContext.partnerId) {
      trackEvent({
        id: 'notifications.drawer.settings.clicked',
        description: 'User clicked settings button from drawer',
        data: {
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          currentFilter: activeFilter || NotificationFilterType.All,
          timestamp: new Date().toISOString(),
        },
      })
      history.push(
        routes['notifications-settings'].replace(':partner_id', partnerContext.partnerId)
      )
      onClose()
    }
  }, [history, partnerContext.partnerId, activeFilter, account.id, onClose])

  const handleFilterChange = useCallback(
    (value: string) => {
      const previousFilter = previousFilterRef.current || activeFilter
      trackEvent({
        id: 'notifications.drawer.filter.selected',
        description: 'User selected a notification filter',
        data: {
          filterType: value,
          previousFilter,
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
      previousFilterRef.current = activeFilter || null
      setActiveFilter(value)
    },
    [setActiveFilter, activeFilter, partnerContext.partnerId, account.id]
  )

  const selected = activeFilter || NotificationFilterType.All

  return (
    <Panel>
      <Header>
        <Text typography="title" css={{ margin: 0, flex: 1 }}>
          {i18n.title}
        </Text>
        <IconButton
          icon={GearIcon}
          variant="tertiary"
          compact
          onClick={onSettingsClick}
          aria-label={i18n.settingsButton}
        />
      </Header>
      <FilterRow>
        <FilterSelectorRow
          options={filters}
          value={selected}
          onChange={handleFilterChange}
          compact
        />
      </FilterRow>
      <FeedScroll>
        <NotificationFeed
          account={account}
          filter={selected as NotificationFilterType}
          filterLabel={filters.find(f => f.value === selected)?.label || ''}
          pollingInterval={isOpen ? 30000 : 0}
          isOpen={isOpen}
        />
      </FeedScroll>
    </Panel>
  )
}
